import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3ccf7a82"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "is-flex-direction-column" }
const _hoisted_2 = {
  key: 0,
  class: "columns is-variable is-3"
}
const _hoisted_3 = { class: "column is-7" }
const _hoisted_4 = {
  id: "page-title",
  class: "title is-4 is-uppercase has-text-weight-medium is-inline"
}
const _hoisted_5 = { class: "title is-6" }
const _hoisted_6 = ["src"]
const _hoisted_7 = { class: "column" }

import { Delete } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox, TabsPaneContext } from 'element-plus';
import { onMounted, Ref, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import AnalysisService from '@/Analysis/analysis.service';
import AnalysisCriteria from '@/components/AnalysisCriteria.vue';
import AnalysisHistory from '@/components/AnalysisHistory.vue';
import AnalysisStatusBadge from '@/components/AnalysisStatusBadge.vue';
import AnalysisViewer from '@/components/AnalysisViewer.vue';
import MemberAvatar from '@/components/MemberAvatar.vue';
import SimilarAnalysesList from '@/components/SimilarAnalysesList.vue';
import { EntrySelection } from '@/Entry/entry.entity';
import { canDeleteAnalysis } from '@/helpers/permission';
import { Criterion } from '@/Litigation/criterion.entity';
import { useAnalysisActionsStore } from '@/stores/analysis-actions.store';
import { useSimilarAnalysesStore } from '@/stores/similar-analyses.store';


export default /*#__PURE__*/_defineComponent({
  __name: 'AnalysisForm',
  props: {
    analysisId: {
        type: String,
        required: true,
    },
},
  setup(__props) {

const props = __props;

const { t } = useI18n();
const router = useRouter();
const similarAnalysesStore = useSimilarAnalysesStore();
const analysisActionsStore = useAnalysisActionsStore();



const { analysisId } = toRefs(props);

const activeLeftTab = ref('text');
const activeRightTab = ref('criteria');
const analysis = ref();
const pdf = ref();
const annotatedEntry: Ref<{ criterion: Criterion; occurrence: Number }> = ref({ criterion: null, occurrence: null });
const analysisViewerRef = ref();
const analysisCriteriaRef = ref();

const analysisService = new AnalysisService();

const loadAnalysis = async () => {
    analysis.value = await analysisService.findAnalysisById(analysisId.value);
};

onMounted(async () => {
    // Reset any similar set left in the singleton store by a previously viewed analysis.
    similarAnalysesStore.clearSimilar();
    try {
        await loadAnalysis();
        // Loaded once: the similar-analyses set does not change from annotation/status actions.
        await similarAnalysesStore.loadSimilar(analysisId.value);
    } catch (error) {
        const { message } = error as { message: string };
        ElMessage.error(message);
    }
});

const openLeftTab = async (context: TabsPaneContext) => {
    if (context.paneName === 'pdf') {
        if (pdf.value === undefined) {
            const file = await analysisService.findFileAnalysisById(analysisId.value);
            pdf.value = window.URL.createObjectURL(file);
        }
    }
};

const deleteAnalysis = async (): Promise<void> => {
    try {
        await ElMessageBox.confirm(t('analysis.deleteConfirmMessage'), t('analysis.deleteConfirmTitle'), {
            type: 'warning',
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        });

        try {
            await analysisActionsStore.deleteAnalysis(analysisId.value);
            goToAnalysisList();
            ElMessage.success(t('analysis.deleteConfirmed'));
        } catch (error) {
            const { message } = error as { message: string };
            ElMessage.error(message);
        }
    } catch (error) {
        ElMessage.info(t('analysis.deleteCanceled'));
    }
};

const goToAnalysisList = (): void => {
    router.push({
        name: 'analysis-list',
        params: { litigationId: analysis.value.litigation.litigationId },
    });
};

const onStartAnnotation = (criterion: Criterion, occurrence: Number) => {
    annotatedEntry.value = { criterion, occurrence };
};

const onStopAnnotation = () => {
    annotatedEntry.value = { criterion: null, occurrence: null };
};

const onLocateSelection = (criterion: Criterion, occurrence: number, selection: EntrySelection): void => {
    analysisViewerRef.value.scrollToSelection(criterion, occurrence, selection);
};

const onCreateSelection = async (selection: string) => {
    await loadAnalysis();
    await analysisCriteriaRef.value.notifySelection(
        annotatedEntry.value.criterion,
        annotatedEntry.value.occurrence,
        selection,
    );
    await analysisViewerRef.value.highlightAllSelections();
};

const onRemoveSelection = async () => {
    await loadAnalysis();
    await analysisViewerRef.value.highlightAllSelections();
};

const onChangeStatus = async () => {
    await loadAnalysis();
};

const onSetEntry = async () => {
    await loadAnalysis();
};

const onFixError = async () => {
    await loadAnalysis();
};

const onIgnoreError = async () => {
    await loadAnalysis();
};

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_el_badge = _resolveComponent("el-badge")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_affix = _resolveComponent("el-affix")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", null, [
      (analysis.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, [
                _createVNode(_component_router_link, {
                  to: { name: 'analysis-list', params: { litigationId: analysis.value.litigation.litigationId } }
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(analysis.value.litigation.name), 1)
                  ]),
                  _: 1
                }, 8, ["to"]),
                _createTextVNode(" / " + _toDisplayString(_ctx.$t('common.decision')) + " ", 1),
                (analysis.value && _unref(canDeleteAnalysis)(analysis.value))
                  ? (_openBlock(), _createBlock(_component_el_button, {
                      key: 0,
                      type: "danger",
                      icon: _unref(Delete),
                      plain: "",
                      class: _normalizeClass(['is-small']),
                      title: _ctx.$t('analysis.deleteAnalysis'),
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (deleteAnalysis()))
                    }, null, 8, ["icon", "title"]))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("p", _hoisted_5, [
                _createTextVNode(_toDisplayString(analysis.value.decision.fileName) + "  ", 1),
                _createVNode(AnalysisStatusBadge, {
                  status: analysis.value.status
                }, null, 8, ["status"]),
                _createTextVNode("  "),
                _createVNode(MemberAvatar, {
                  member: analysis.value.owner
                }, null, 8, ["member"])
              ]),
              _createVNode(_component_el_tabs, {
                modelValue: activeLeftTab.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeLeftTab).value = $event)),
                type: "border-card",
                onTabClick: openLeftTab
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_el_tab_pane, {
                    name: "text",
                    label: _unref(t)('analysis.rawTextTab')
                  }, {
                    default: _withCtx(() => [
                      _createVNode(AnalysisViewer, {
                        ref_key: "analysisViewerRef",
                        ref: analysisViewerRef,
                        analysis: analysis.value,
                        "annotated-entry": annotatedEntry.value,
                        onCreateSelection: onCreateSelection
                      }, null, 8, ["analysis", "annotated-entry"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_component_el_tab_pane, {
                    name: "pdf",
                    label: _unref(t)('analysis.pdfTab'),
                    disabled: analysis.value.decision.fingerprint === null
                  }, {
                    default: _withCtx(() => [
                      (pdf.value)
                        ? (_openBlock(), _createElementBlock("iframe", {
                            key: 0,
                            src: pdf.value,
                            width: "100%",
                            height: "700px"
                          }, null, 8, _hoisted_6))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["label", "disabled"])
                ]),
                _: 1
              }, 8, ["modelValue"])
            ]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(_component_el_affix, null, {
                default: _withCtx(() => [
                  _createVNode(_component_el_tabs, {
                    modelValue: activeRightTab.value,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((activeRightTab).value = $event)),
                    type: "border-card"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_tab_pane, {
                        name: "criteria",
                        label: _unref(t)('analysis.criteriaTab')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(AnalysisCriteria, {
                            ref_key: "analysisCriteriaRef",
                            ref: analysisCriteriaRef,
                            analysis: analysis.value,
                            onStartAnnotation: onStartAnnotation,
                            onStopAnnotation: onStopAnnotation,
                            onLocateSelection: onLocateSelection,
                            onRemoveSelection: onRemoveSelection,
                            onChangeStatus: onChangeStatus,
                            onSetEntry: onSetEntry,
                            onFixError: onFixError,
                            onIgnoreError: onIgnoreError
                          }, null, 8, ["analysis"])
                        ]),
                        _: 1
                      }, 8, ["label"]),
                      _createVNode(_component_el_tab_pane, {
                        name: "history",
                        label: _unref(t)('analysis.statusesTab')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(AnalysisHistory, {
                            statuses: analysis.value.statuses
                          }, null, 8, ["statuses"])
                        ]),
                        _: 1
                      }, 8, ["label"]),
                      (_unref(similarAnalysesStore).similar.length > 0)
                        ? (_openBlock(), _createBlock(_component_el_tab_pane, {
                            key: 0,
                            name: "similar"
                          }, {
                            label: _withCtx(() => [
                              _createVNode(_component_el_badge, {
                                value: _unref(similarAnalysesStore).similar.length,
                                type: "warning",
                                title: _unref(t)('analysis.similarAnalysesHint')
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)('analysis.similarAnalysesTab')), 1)
                                ]),
                                _: 1
                              }, 8, ["value", "title"])
                            ]),
                            default: _withCtx(() => [
                              _createVNode(_component_el_alert, {
                                title: _unref(t)('analysis.similarAnalysesHint'),
                                type: "warning",
                                closable: false,
                                "show-icon": "",
                                class: "mb-3"
                              }, null, 8, ["title"]),
                              _createVNode(SimilarAnalysesList)
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ]),
                _: 1
              })
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})